import { useEffect, useRef } from 'react'
import { accessToken } from './supabase'
import type { FeedEventMap, FeedEventName } from './contract'

/**
 * Subscribes to a session's SSE feed and dispatches typed events to handlers.
 *
 * This is the shared core every grammar reuses. It owns transport only —
 * connect, parse, dispatch — and holds no display opinion, so the Inhabit feed
 * and the desk's traffic can render the same stream in their own house.
 *
 * Handlers are held in a ref, so a caller may pass fresh closures every render
 * without tearing down the EventSource (which would drop the stream and re-fire
 * `hello`).
 */
export type FeedHandlers = {
  [K in FeedEventName]?: (payload: FeedEventMap[K]) => void
} & {
  /** Transport-level open/close, distinct from the server's `error` event. */
  onOpen?: () => void
  onDisconnect?: () => void
}

const EVENT_NAMES: FeedEventName[] = [
  'hello',
  'chunk-gen-started',
  'chunk-gen-completed',
  'narrator-started',
  'narrator-completed',
  'scene-card',
  'message',
  'messaging-render-started',
  'messaging-render-completed',
  'error',
]

export function useSessionFeed(
  sessionId: string | null,
  handlers: FeedHandlers,
): void {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    if (!sessionId) return

    let source: EventSource | null = null
    let cancelled = false

    // EventSource cannot send an Authorization header — the browser API has no
    // way to add one — so the stream authenticates with a query parameter.
    // Heimar.Api accepts `access_token` on this route only.
    void (async () => {
      const token = await accessToken()
      if (cancelled) return

      const base = `/api/session/${sessionId}/stream`
      source = new EventSource(
        token ? `${base}?access_token=${encodeURIComponent(token)}` : base,
      )

      for (const name of EVENT_NAMES) {
        source.addEventListener(name, (ev: Event) => {
          const data = (ev as MessageEvent).data
          // The server's `error` event carries a payload; a transport failure
          // fires the same name with none. Only the former is an engine error —
          // the latter means the connection dropped.
          if (!data) {
            if (name === 'error') handlersRef.current.onDisconnect?.()
            return
          }
          let payload: unknown
          try {
            payload = JSON.parse(data)
          } catch {
            return // malformed frame — ignore rather than kill the stream
          }
          const handler = handlersRef.current[name] as
            | ((p: unknown) => void)
            | undefined
          handler?.(payload)
        })
      }

      source.onopen = () => handlersRef.current.onOpen?.()
    })()

    return () => {
      cancelled = true
      // Closing drops every listener with it, so they need no separate removal.
      source?.close()
    }
  }, [sessionId])
}
